<template>
  <div class="card categories-bars-widget">
    <WidgetHeader
      :title="widgetTitle"
      :onBack="canGoBack ? back : null"
      :onBackText="$t('widgetCategoriesBars.title')"
      :navigation="navigation"
    >
      <template #right>
        <PulsFormSwitch
          v-if="showCurrentDataToggle"
          v-model="showOnlyCurrentData"
          :option="{
            title: $t('widgetCategoriesBars.toggles.currentSurveysOnly.title'),
            description: $t(
              'widgetCategoriesBars.toggles.currentSurveysOnly.description',
              {
                surveyName: surveyTitle
              }
            ),
            value: 'show-current-only'
          }"
          labelPlacement="left"
        />
        <span
          v-if="currentCategory && showOnlyCurrentData && surveyIsAggregateV2"
        >
          {{
            $t(
              'widgetCategoriesBars.toggles.currentSurveysOnly.valueDescription',
              { surveyName: surveyTitle }
            )
          }}
        </span>
      </template>
    </WidgetHeader>
    <WidgetBody
      v-if="!singleStatItem"
      :id="id"
      :onSwiperInitCallback="onSwiperInit"
      :initialSlideIndex="widgetSliderState.index"
      :updateSliderIndex="updateSliderIndex"
      :viewMode="viewMode"
      @next-transition-start="updateSliderIndex"
      @prev-transition-start="updateSliderIndex"
    >
      <template #slide-0>
        <div v-if="!singleStatItem && categoriesForTable.length">
          <div v-if="shouldSplitTableByTags">
            <div
              v-for="tagName in Object.keys(categoriesForTableGroupedByTagName)"
              :key="tagName"
              class="mb-8"
            >
              <h3 class="tag-title">
                {{ tagsAndCategories[tagName].title }}
              </h3>
              <CategoriesBarsTable
                :id="id"
                :categories="categoriesForTableGroupedByTagName[tagName]"
                :data="data"
                :theme="theme"
                :surveyName="surveyName"
                :userIsAdminForCurrentGroupOrSuperAdmin="
                  userIsAdminForCurrentGroupOrSuperAdmin
                "
                :shouldShowColorSegments="shouldShowColorSegments"
                :shouldHideAverages="shouldHideAverages"
                :onOpenCategoryDetails="openCategoryDetails"
              />
            </div>
          </div>
          <div v-else>
            <CategoriesBarsTable
              :id="id"
              :categories="categoriesForTable"
              :data="data"
              :theme="theme"
              :surveyName="surveyName"
              :userIsAdminForCurrentGroupOrSuperAdmin="
                userIsAdminForCurrentGroupOrSuperAdmin
              "
              :shouldShowColorSegments="shouldShowColorSegments"
              :shouldHideAverages="shouldHideAverages"
              :onOpenCategoryDetails="openCategoryDetails"
            />
          </div>
        </div>
        <div v-else>
          <p class="info-title d-flex align-items-center mb-0 fs-5">
            <i class="bi bi-info-circle-fill text-primary fs-3 me-3"></i>
            {{ $t('widgetCategoriesBars.notEnoughResponses') }}
          </p>
        </div>
      </template>
      <template #slide-1>
        <CategoryDetails
          :id="id"
          :category="currentCategory"
          :followUpQuestionsState="
            widgetSliderState.info?.followUpQuestionsState
          "
          :isIndividualGroup="isIndividualGroup"
          :shouldHideOverviewForCategory="shouldHideOverviewForSelectedCategory"
          :surveyName="surveyName"
          :onSelectCategoryTab="onSelectCategoryTab"
          :selectedTab="selectedTabForDetails"
          :shouldShowResultsPerPerson="shouldShowResultsPerPersonForCategory()"
          :resultsPerPerson="
            shouldShowResultsPerPersonForCategory() &&
            currentCategory &&
            categoriesResultsPerPerson &&
            categoriesResultsPerPerson[currentCategory.name]
          "
          :userIsAdminForCurrentGroupOrSuperAdmin="
            userIsAdminForCurrentGroupOrSuperAdmin
          "
          :theme="theme"
          :updateFollowupQuestionStateForQuestion="
            updateFollowupQuestionStateForQuestion
          "
          :loadQuestionResponses="onLoadQuestionResponses"
          :singleStatItem="singleStatItem"
          :userInfo="userInfo"
          :selectedGroup="selectedGroup"
          :loadingQuestionResponses="loadingQuestionResponses"
        />
      </template>
    </WidgetBody>
    <div v-else class="card-body widget-slider">
      <CategoryDetails
        :id="id"
        :category="currentCategory"
        :followUpQuestionsState="widgetSliderState.info?.followUpQuestionsState"
        :isIndividualGroup="isIndividualGroup"
        :shouldHideOverviewForCategory="shouldHideOverviewForSelectedCategory"
        :surveyName="surveyName"
        :onSelectCategoryTab="onSelectCategoryTab"
        :selectedTab="selectedTabForDetails"
        :shouldShowResultsPerPerson="shouldShowResultsPerPersonForCategory()"
        :resultsPerPerson="
          shouldShowResultsPerPersonForCategory() &&
          currentCategory &&
          categoriesResultsPerPerson &&
          categoriesResultsPerPerson[currentCategory.name]
        "
        :userIsAdminForCurrentGroupOrSuperAdmin="
          userIsAdminForCurrentGroupOrSuperAdmin
        "
        :theme="theme"
        :loadQuestionResponses="onLoadQuestionResponses"
        :singleStatItem="singleStatItem"
        :userInfo="userInfo"
        :selectedGroup="selectedGroup"
        :loadingQuestionResponses="loadingQuestionResponses"
      />
    </div>
  </div>
</template>

<script>
import commonWidgetProps from '@/components/mixins/common-widget-props'
import { requestTypes } from '@/constants/request-types'
import { getForceIncludedAndNotExcludedCategories } from '@/helpers/results-helper'
import CategoriesBarsTable from './subcomponents/CategoriesBarsTable.vue'
import CategoryDetails from './subcomponents/CategoryDetails.vue'
import WidgetHeader from '@/components/widgets/shared/WidgetHeader.vue'
import WidgetBody from '@/components/widgets/shared/WidgetBody.vue'
import { errorTypes } from '@/constants/error-types'
import { processAmounts } from '@/helpers/results-helper'

export const categoriesBarsWidgetInformation = () => {
  return {
    requests: {
      [requestTypes.SURVEY_DETAILS]: {},
      [requestTypes.TAGS]: {},
      [requestTypes.CATEGORIES]: {},
      [requestTypes.QUESTIONS]: {},
      [requestTypes.TREND_CATEGORIES]: {},
      [requestTypes.TREND_QUESTIONS]: {}
      // categories: {}
    }
  }
}

export const categoriesBarsWidgetSkeleton = () => {
  return {
    header: {
      height: 2
    },
    body: [1, 2, 3, 4].map(() => {
      return { cols: [{ height: 2 }, { height: 2 }] }
    })
  }
}

const CategoriesBarsWidget = {
  name: 'CategoriesBarsWidget',
  props: {
    surveyDetails: {
      type: Object,
      default: () => {}
    },
    tags: {
      type: Object,
      default: () => {}
    },
    categories: {
      type: Object,
      default: () => {}
    },
    categoriesResultsPerPerson: {
      type: Object,
      default: () => {}
    },
    questions: {
      type: Object,
      default: () => {}
    },
    trendCategories: {
      type: Object,
      default: () => {}
    },
    trendQuestions: {
      type: Object,
      default: () => {}
    },
    updateWidgetRequests: {
      type: Function,
      default: () => null
    },
    // TODO: this should probably be re-worked to be more flexible. Do we always want to hide freetext if singleStatItem?
    singleStatItem: {
      type: Object,
      default: () => {}
    }
  },
  components: {
    CategoriesBarsTable,
    CategoryDetails,
    WidgetBody,
    WidgetHeader
  },
  mixins: [commonWidgetProps],
  data() {
    return {
      currentCategory: null,
      selectedTabForDetails: 'overall',
      showOnlyCurrentData: false,
      loadingQuestionResponses: {}
    }
  },
  mounted() {
    if (this.shouldDefaultToQuestionsTab) {
      this.selectedTabForDetails = 'questions'
    }
    const noCategoryHasResponses =
      !this.categoriesForTable?.length ||
      this.categoriesForTable.every((c) => !c.numberOfResponses)
    if (noCategoryHasResponses) {
      this.deregisterWidget({
        id: this.id,
        info: {
          reason: errorTypes.NOT_ENOUGH_DATA,
          hideWidgetWhenNoSurveyResults: true
        },
        groupName: this.selectedGroupName
      })
    } else if (
      // this.widgetSliderState.index > 0 &&
      this.widgetSliderState.info
    ) {
      const savedCategory = this.widgetSliderState.info.currentCategory
      const questions = this.getQuestionsForCategory(savedCategory.name)
      // const propertiesInSavedButNotInCategory = Object.keys(
      //   savedCategory
      // ).filter((key) => !this.categories[savedCategory.name][key])
      this.currentCategory = {
        ...savedCategory,
        ...this.categories[savedCategory.name],
        averages: this.trendCategories[savedCategory.name]?.averages,
        regressionValues:
          this.trendCategories[savedCategory.name]?.regressionValues,
        questions
      }
      if (this.widgetSliderState.info.selectedTabName) {
        this.selectedTabForDetails = this.widgetSliderState.info.selectedTabName
      }
    } else if (this.singleStatItem) {
      const categoryWithDetails = this.categoriesForTable.find(
        (c) => c.name === this.singleStatItem.name
      )
      this.openCategoryDetails(categoryWithDetails)
    }
  },
  computed: {
    atleastOneCategoryHasMultipleSurveys() {
      return this.categoriesForTable.some((c) =>
        c.surveyNames.some((surveyName) => surveyName !== this.surveyName)
      )
    },
    canGoBack() {
      if (this.singleStatItem || this.currentCategory !== null) {
        return true
      }
      return false
    },
    showCurrentDataToggle() {
      return (
        !this.currentCategory &&
        (this.showOnlyCurrentData ||
          (this.surveyIsAggregateV2 &&
            this.atleastOneCategoryHasMultipleSurveys))
      )
    },
    userIsAdminForCurrentGroupOrSuperAdmin() {
      return this.userIsGroupAdmin || this.userIsPulsAdmin
    },
    categoriesForTable() {
      return this.forceIncludedAndNotExcludedCategories.reduce(
        (categoriesWithData, category) => {
          const catStat = this.categories[category.name]
          if (catStat) {
            const keepCategory =
              !this.showOnlyCurrentData ||
              (catStat.surveyNames || []).some(
                (surveyName) => surveyName === this.surveyName
              )
            if (keepCategory) {
              categoriesWithData.push({
                ...catStat,
                internalCategoryOrder: categoriesWithData.length,
                name: category.name,
                averages: this.trendCategories[category.name]?.averages || [],
                regressionValues:
                  this.trendCategories[category.name]?.regressionValues || [],
                title: this.$tr(catStat.messages, 'title'),
                colors:
                  this.shouldShowColorSegments &&
                  this.trendCategories[category.name]
                    ? this.getColorSegmentsForCategory({ category: catStat })
                    : {}
              })
            }
          }
          return categoriesWithData
        },
        []
      )
    },
    categoriesForTableGroupedByTagName() {
      const categoriesByTag = {}
      if (
        this.tagsAndCategories &&
        Object.keys(this.tagsAndCategories).length
      ) {
        for (const tagName of Object.keys(this.tagsAndCategories)) {
          const categoriesForTag = this.categoriesForTable.filter((c) =>
            this.tagsAndCategories[tagName]?.categories?.includes(c.name)
          )
          if (categoriesForTag?.length) {
            categoriesByTag[tagName] = categoriesForTag
          }
        }
      }
      return categoriesByTag
    },
    currentCategoryHasAtLeastOneZeroToTenQuestion() {
      return Object.keys(this.currentCategory?.questions || {})?.some(
        (questionName) =>
          this.currentCategory.questions[questionName].type === 'zeroToTen'
      )
    },
    forceIncludedAndNotExcludedCategories() {
      const categoriesDetails = this.surveyDetails?.details?.categories || []
      return getForceIncludedAndNotExcludedCategories({
        categoriesResult: this.categories,
        surveyDetailsCategories: categoriesDetails,
        categoryOptions: this.data
      })
    },
    isHarassmentCategory() {
      return this.currentCategory?.name.includes('harassment')
    },
    isIndividualGroup() {
      return this.selectedGroup?.numberOfUsers === 1
    },
    navigation() {
      if (
        !this.singleStatItem &&
        this.currentCategory &&
        this.categoriesForTable?.length > 1
      ) {
        const internalCategoryOrder = this.currentCategory.internalCategoryOrder

        const leftOfCategoryOrder =
          internalCategoryOrder === 0
            ? this.categoriesForTable.length - 1
            : internalCategoryOrder - 1
        const leftOfcategory = this.categoriesForTable.find((category) => {
          return category.internalCategoryOrder === leftOfCategoryOrder
        })

        const rightOfCategoryOrder =
          internalCategoryOrder === this.categoriesForTable.length - 1
            ? 0
            : internalCategoryOrder + 1
        const rightOfcategory = this.categoriesForTable.find((category) => {
          return category.internalCategoryOrder === rightOfCategoryOrder
        })

        return {
          left: {
            // title: this.$tr(left.messages, 'title'),
            icon: 'bi-arrow-left',
            value: leftOfcategory,
            onClick: this.openCategoryDetails
          },
          right: {
            // title: this.$tr(right.messages, 'title'),
            icon: 'bi-arrow-right',
            value: rightOfcategory,
            onClick: this.openCategoryDetails
          }
        }
      } else {
        return {}
      }
    },
    shouldDefaultToQuestionsTab() {
      return (
        this.shouldHideOverviewForSelectedCategory ||
        this.isHarassmentCategory ||
        (this.currentCategory &&
          !this.currentCategoryHasAtLeastOneZeroToTenQuestion &&
          !this.shouldShowResultsPerPersonForCategory)
      )
    },
    shouldSplitTableByTags() {
      return this.tagsForCategories.length > 0 // TODO: Re-enable this, used for Friskkollen
    },
    shouldHideOverviewForSelectedCategory() {
      return (
        this.isIndividualGroup ||
        this.tags?.participation?.amount === 1 ||
        this.data?.hideOverviewForCategories?.some(
          (categoryName) => categoryName === this.currentCategory?.name
        )
      )
    },
    shouldHideAverages() {
      return this.data.shouldHideAverages || false
    },
    shouldShowColorSegments() {
      return this.data.shouldShowColorSegments || false
    },
    surveyTitle() {
      return this.$tr(this.surveyDetails.details.messages, 'title')
    },
    tagsAndCategories() {
      return this.tagsForCategories.reduce((tags, tagName) => {
        const categoryNamesForTag = (
          this.surveyDetails.details.categories || []
        ).reduce((categories, c) => {
          const categoryBelongsToTag = c.questions.find((q) =>
            q.tags.includes(tagName)
          )
          if (categoryBelongsToTag) {
            categories.push(c.name)
          }
          return categories
        }, [])
        tags[tagName] = {
          categories: categoryNamesForTag,
          title: this.$tr(
            this.surveyDetails.details.messages,
            `tags.${tagName}.title`
          )
        }
        return tags
      }, {})
    },
    tagsForCategories() {
      return this.data.tagsForCategories || []
    },
    widgetTitle() {
      if (this.currentCategory) {
        if (this.singleStatItem) {
          return `${this.singleStatItem.group.title} - ${this.$tr(this.currentCategory.messages, 'title')}`
        } else {
          return `${this.$tr(this.currentCategory.messages, 'title')}`
        }
      } else if (Object.keys(this.categories).length) {
        return this.$t('widgetCategoriesBars.title')
      } else {
        return null
      }
    }
  },
  methods: {
    back() {
      if (this.singleStatItem) {
        this.singleStatItem.onBack()
      } else {
        this.swiperInstance.slidePrev()
      }
      this.currentCategory = null
    },
    getColorSegmentsForCategory({ category }) {
      const colorNameForValue = (value) => {
        if (value < 4) {
          return 'numberOfRed'
        } else if (value >= 7) {
          return 'numberOfGreen'
        } else {
          return 'numberOfYellow'
        }
      }

      const result = {
        numberOfRed: 0,
        numberOfYellow: 0,
        numberOfGreen: 0,
        numberInTotal: 0,
        percentageRed: 0,
        percentageYellow: 0,
        percentageGreen: 0
      }

      processAmounts(result, category, (target, i, count) => {
        const colorCategory = colorNameForValue(i)
        target[colorCategory] += count
        target.numberInTotal += count
      })

      if (result.numberInTotal > 0) {
        result.percentageRed = Math.round(
          (result.numberOfRed / result.numberInTotal) * 100
        )
        result.percentageYellow = Math.round(
          (result.numberOfYellow / result.numberInTotal) * 100
        )
        result.percentageGreen = Math.round(
          (result.numberOfGreen / result.numberInTotal) * 100
        )
      }
      return result
    },
    getQuestionsForCategory(categoryName) {
      const categoryWithDetails = this.surveyDetails.details.categories.find(
        (c) => c.name === categoryName
      )
      return categoryWithDetails.questions.reduce((res, q) => {
        const statItem = this.questions[q.name]
        if (!statItem) {
          return res
        }
        // If showOnlyCurrentData is enabled, only include questions that
        // explicitly include the current surveyName in their surveyNames array.
        // If surveyNames is missing, treat it as current-only and include it.
        const keepQuestion = !this.showOnlyCurrentData
          ? true
          : (statItem.surveyNames || []).includes(this.surveyName)
        if (keepQuestion) {
          res[q.name] = {
            ...statItem,
            name: q.name,
            averages: this.trendQuestions[q.name]?.averages || [],
            regressionValues:
              this.trendQuestions[q.name]?.regressionValues || []
          }
        }

        return res
      }, {})
    },
    updateFollowupQuestionStateForQuestion({
      questionName,
      followUpQuestionsState = {}
    }) {
      // when loading question responses, we need to keep track of which
      // questions are currently showing follow up questions
      this.updateWidgetSliderState({
        id: this.id,
        index: this.swiperInstance?.activeIndex || 0,
        info: {
          currentCategory: this.currentCategory,
          selectedTabName: this.selectedTabForDetails,
          followUpQuestionsState: {
            ...this.widgetSliderState.info.followUpQuestionsState,
            [questionName]: { ...followUpQuestionsState }
          }
        }
      })
    },
    onLoadQuestionResponses(question) {
      this.loadingQuestionResponses = {
        ...this.loadingQuestionResponses,
        [question.name]: true
      }
      this.updateWidgetRequests({
        mode: 'add',
        id: this.id,
        requests: {
          [requestTypes.QUESTION_RESULTS_FOR_SURVEY]: {
            params: {
              [this.currentCategory.name]: {
                [question.name]: {}
              },
              hideWidgetWhenLoading: false
            }
          }
        }
      })
    },
    openCategoryDetails(category) {
      const questions = this.getQuestionsForCategory(category.name)
      const links = this.data.links?.[category.name] || []
      this.currentCategory = { ...category, questions, links }
      // We need to update the saved category in case the category
      // we are navigating to triggers updateWidgetRequests
      this.updateWidgetSliderState({
        id: this.id,
        index: this.swiperInstance?.activeIndex || 0,
        info: {
          currentCategory: this.currentCategory,
          selectedTabName: this.selectedTabForDetails,
          followUpQuestionsState: {
            ...(this.widgetSliderState.info?.followUpQuestionsState || {})
          }
        }
      })
      if (this.swiperInstance) {
        // get questions that are in the selected category
        this.swiperInstance.slideNext()
      }
    },
    onPrevTransitionStart(swiper) {
      this.currentCategory = null
      this.updateWidgetSliderState({
        id: this.id,
        index: swiper.activeIndex
      })
    },
    onSwiperInit(swiper) {
      this.swiperInstance = swiper
    },
    shouldShowResultsPerPersonForCategory(categoryName) {
      let categoryNameToUse = categoryName
      if (!categoryName) {
        categoryNameToUse = this.currentCategory?.name
      }
      return (
        this.tags?.participation?.amount > 1 &&
        this.data.showResultsPerPerson?.categories?.some((name) => {
          return name === categoryNameToUse
        })
      )
    },
    updateSliderIndex(swiper) {
      this.updateWidgetSliderState({
        id: this.id,
        index: swiper.activeIndex,
        info: {
          currentCategory: this.currentCategory,
          selectedTabName: this.selectedTabForDetails,
          followUpQuestionsState: {
            ...(this.widgetSliderState.info?.followUpQuestionsState || {})
          }
        }
      })
    },
    updateSliderHeight() {
      setTimeout(() => {
        if (this.swiperInstance?.params) {
          this.swiperInstance.updateAutoHeight(200)
        }
      }, 25)
    },
    onSelectCategoryTab(selectedTabName) {
      if (selectedTabName) {
        this.selectedTabForDetails = selectedTabName
        this.updateWidgetSliderState({
          id: this.id,
          index: this.swiperInstance?.activeIndex || 0,
          info: {
            currentCategory: this.currentCategory,
            selectedTabName,
            followUpQuestionsState: {
              ...(this.widgetSliderState.info?.followUpQuestionsState || {})
            }
          }
        })
      }
      this.updateSliderHeight()
    }
  },
  watch: {
    shouldDefaultToQuestionsTab() {
      if (this.shouldDefaultToQuestionsTab) {
        this.selectedTabForDetails = 'questions'
      }
    },
    categoriesForTable(newCategories, oldCategories) {
      const newLen = newCategories?.length || 0
      const oldLen = oldCategories?.length || 0
      if (newLen !== oldLen) {
        this.updateSliderHeight()
      }
    },
    categoriesResultsPerPerson() {
      if (this.categoriesResultsPerPerson?.[this.currentCategory.name]) {
        this.updateSliderHeight()
      }
    },
    currentCategory(newCategory, lastCategory) {
      const shouldRemoveRequestForResultsPerPersonForLastCategory =
        lastCategory &&
        this.shouldShowResultsPerPersonForCategory(lastCategory.name) &&
        this.categoriesResultsPerPerson?.[lastCategory.name]

      if (newCategory) {
        const shouldRequestResultsPerPersonForNewCategory =
          this.shouldShowResultsPerPersonForCategory(newCategory.name) &&
          !this.categoriesResultsPerPerson?.[newCategory.name]
        if (shouldRequestResultsPerPersonForNewCategory) {
          this.updateWidgetRequests({
            mode: 'add',
            id: this.id,
            requests: {
              [requestTypes.CATEGORIES_RESULTS_PER_PERSON]: {
                params: {
                  [newCategory.name]: {}
                }
              }
            }
          })
        }
      } else if (shouldRemoveRequestForResultsPerPersonForLastCategory) {
        // unregister results per person and question results when backing out of category
        this.updateWidgetRequests({
          mode: 'remove',
          id: this.id,
          requests: {
            [requestTypes.CATEGORIES_RESULTS_PER_PERSON]: {},
            [requestTypes.QUESTION_RESULTS_FOR_SURVEY]: {}
          }
        })
      }

      if (
        newCategory &&
        lastCategory &&
        newCategory.name !== lastCategory.name
      ) {
        this.updateWidgetSliderState({
          id: this.id,
          index: this.swiperInstance?.activeIndex || 0,
          info: {
            currentCategory: this.currentCategory,
            selectedTabName: this.selectedTabForDetails,
            followUpQuestionsState: {
              ...(this.widgetSliderState.info?.followUpQuestionsState || {})
            }
          }
        })
      }
      this.updateSliderHeight()
    },
    questions(newQuestions) {
      if (this.currentCategory) {
        Object.keys(this.loadingQuestionResponses).forEach((questionName) => {
          const questionWasLoading = this.loadingQuestionResponses[questionName]
          const newQuestion = newQuestions[questionName]
          if (
            questionWasLoading &&
            (newQuestion?.answers?.length || newQuestion?.error)
          ) {
            // mark question as no longer loading
            this.loadingQuestionResponses = {
              ...this.loadingQuestionResponses,
              [questionName]: false
            }
            // update the currentCategory questions with new answers
            this.currentCategory.questions = {
              ...this.currentCategory.questions,
              [questionName]: newQuestion
            }
            this.updateSliderHeight()
          }
        })
      }
    }
  }
}

export default CategoriesBarsWidget
</script>

<style lang="scss" scoped>
.tag-title {
  font-weight: 500;
  font-size: 1.275rem;
  color: var(--kt-text-dark);
}
</style>
